import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ViewBasePage } from '@shared/pages';

import {
    AccountsService,
    DealershipService,
    UsersService,
    UserAccountMapsService
} from '@shared/services';

import type { TAccount, TResponse } from '@shared/models';

@Component({
    'selector': 'app-accounts',
    'templateUrl': './accounts.page.html',
    'styleUrls': ['./accounts.page.scss'],
    'standalone': true,
    'imports': [ IonicModule, CommonModule, FormsModule],
})
export class AccountsPage extends ViewBasePage implements OnInit, OnDestroy {
    public tableOfContents: TAccount[] = [];
    public headers: TAccount[] = [];
    private subscription: any;

    constructor(private accountsService: AccountsService, private userAccountMapsService: UserAccountMapsService,
        private usersService: UsersService, private dealershipService: DealershipService,
        injector: Injector) {
        super(injector);
    }

    public async ionViewWillEnter() {
        super.ngOnInit();
        // this.onRefresh();
        // this.initMoreMenu();
    }

    // private initMoreMenu(): void {
    // this.resetPopoverMenu();
    // this.pushPopoverMenuItem('Refresh');
    // this.pushPopoverMenuItem('Delete Account');

    // this.moreMenuItemClick((menuItem: any) => {
    //     switch (menuItem.name) {
    //         case 'Refresh':
    //             this.onRefresh();
    //             break;
    // });        
    // }

    public override async ngOnInit() {
        super.ngOnInit();

        const observable = this.accountsService.getObservable();
        // this.subscription = observable.subscribe(async () => {
        this.subscription = observable.subscribe(async () => {
            const toc = await this.accountsService.getTableOfContents();

            this.headers = toc.items;
            this.tableOfContents = toc.items;
            this.onRefresh();
        });
    }

    public override ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        super.ngOnDestroy();
    }

    public onAccountClick(uid: string): void {
        this.router.navigateByUrl('/account/' + uid);
    }

    public onAddAccountClick(): void {
        this.router.navigateByUrl('/accountNew');
    }

    private containsOnlyDigits(str: string): boolean {
        const onlyDigits = str.match(/^[0-9]+$/) !== null;
        return onlyDigits;
    }

    public getSubset(ev) {
        const searchFor = ev.target.value.trim() as string;
        const searchContainsOnlyDigits = this.containsOnlyDigits(searchFor);

        if (!searchFor) {
            this.headers = this.tableOfContents;
            return;
        }

        // Search the account numbers for a match 
        if (searchContainsOnlyDigits) {
            this.headers = this.tableOfContents.filter(account => {
                const idx = account.accountNumber.indexOf(searchFor);
                return (idx > -1);
            });
        }

        // Search the dealership names for a match
        if (!searchContainsOnlyDigits) {
            const searchForLC = searchFor.toLocaleLowerCase();

            this.headers = this.tableOfContents.filter(account => {
                const dealershipName = account.dealershipName.toLowerCase();
                const idx = dealershipName.indexOf(searchForLC);
                return (idx > -1);
            });
        }
    }

    public async onRefresh() {
        const toc = await this.accountsService.getTableOfContents();
        this.headers = toc.items.sort((a, b) => {
            if (a.uid > b.uid) {
                return 1;
            } else {
                return -1;
            }
        });
    }

    public async onDeleteAccountClick(accountNumber: string, dealershipName: string): Promise<void> {
        const title = 'Confirm Account Deletion';
        const message = `Are you sure you want to delete account: ${dealershipName}?`;
        // this.uxHelperService.showConfirmationAlert(title, message, async (response: TResponse) => {
        const response = await this.uxHelperService.showAlert(title, message);
        if (response.okay) {
            const header = 'Enter Account Number';
            const message = 'Account Number: ' + accountNumber;
            const defaultValue = '';
            // this.uxHelperService.promptForString(header, message, defaultValue, async (response: TResponse) => {
            const resp = await this.uxHelperService.promptForString(header, message, defaultValue);
            if (resp.okay && resp.selectedItems[0] === accountNumber) {
                await this.deleteAccount(accountNumber);
            }
        }
    }

    private async deleteAccount(accountNumber: string): Promise<void> {
        await this.uxHelperService.showSpinner();

        // The accountDelete trigger along with the userDelete trigger will delete
        // the entry in the account table of contents and delete the entry in Auth.
        // So, all this routine needs to do is delete the users associated with this account
        // and delete the account itself.
        const users = await this.usersService.getUsersByAccountNumber(accountNumber);
        for (const user of users) {
            await this.usersService.delete(user.uid);
        }

        const account = await this.accountsService.getByUid(accountNumber);
        this.accountsService.delete(account.uid);
        await this.uxHelperService.hideSpinner();
    }
}
